import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Sidebar from '../Sidebar';
import Header from '../Header';
import { useNavigate } from 'react-router-dom';

// Interface for API payload
interface EmailSettingsPayload {
  smtp_host: string;
  smtp_port: number;
  use_tls: boolean;
  use_ssl: boolean;
  smtp_username: string;
  smtp_password?: string; // Make it optional
  from_email: string;
  from_name: string;
  email_signature: string;
  signature_type: 'html' | 'text';
  include_signature: boolean;
}

// Interface for settings
interface EmailSettings {
  allow_self_signed: boolean;
  smtp_host: string;
  smtp_port: number;
  use_tls: boolean;
  use_ssl: boolean;
  smtp_username: string;
  smtp_password: string;
  from_email: string;
  from_name: string;
  email_signature: string;
  signature_type: 'html' | 'text';
  include_signature: boolean;
}

// Interface for test result
interface TestResult {
  success: boolean;
  message: string;
}

// Interface for user
interface User {
  id: number;
  email: string;
  username?: string;
  client_id?: number;
}

const EmailSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState<EmailSettings>({
    allow_self_signed: false,
    smtp_host: '',
    smtp_port: 587,
    use_tls: true,
    use_ssl: false,
    smtp_username: '',
    smtp_password: '',
    from_email: '',
    from_name: '',
    email_signature: '',
    signature_type: 'html',
    include_signature: true
  });
  
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [showSignaturePreview, setShowSignaturePreview] = useState(false);
  const [activeTab, setActiveTab] = useState<'settings' | 'signature'>('settings');

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (error) {
        console.error('Error parsing user from localStorage:', error);
      }
    }
    loadCurrentSettings();
  }, []);

  const loadCurrentSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token found');
        return;
      }
      
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/email/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.configured && response.data.settings) {
        setSettings(prev => ({
          ...prev,
          allow_self_signed: response.data.settings.allow_self_signed || false,
          smtp_host: response.data.settings.smtp_host || '',
          smtp_port: response.data.settings.smtp_port || 587,
          use_tls: response.data.settings.use_tls !== false,
          use_ssl: response.data.settings.use_ssl || false,
          smtp_username: response.data.settings.smtp_username || '',
          // Don't load password for security
          smtp_password: '',
          from_email: response.data.settings.from_email || '',
          from_name: response.data.settings.from_name || '',
          email_signature: response.data.settings.email_signature || '',
          signature_type: response.data.settings.signature_type || 'html',
          include_signature: response.data.settings.include_signature !== false
        }));
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };

    const handleSave = async () => {
  setLoading(true);
  
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      alert('No authentication token found. Please log in again.');
      return;
    }
    
    // CRITICAL: Send ALL fields that the backend expects
    const payload = {
      // Required by backend validation
      allow_self_signed: settings.allow_self_signed || false,
      smtp_host: settings.smtp_host || '',
      smtp_port: settings.smtp_port || 587,
      use_tls: settings.use_tls !== false,
      use_ssl: settings.use_ssl || false,
      smtp_username: settings.smtp_username || '',
      smtp_password: settings.smtp_password || '',
      from_email: settings.from_email || '',
      from_name: settings.from_name || '',
      
      // Optional but should be included
      email_signature: settings.email_signature || '',
      signature_type: settings.signature_type || 'html',
      include_signature: settings.include_signature !== false
    };
    
    console.log('📦 Sending ALL fields:', {
      ...payload,
      smtp_password: '***',
      email_signature: payload.email_signature ? 'Has content' : 'Empty'
    });
    
        // Validate required fields (password is only required on first save)
    if (!payload.smtp_host || !payload.smtp_username || !payload.from_email) {
      alert('Please fill in SMTP Host, SMTP Username, and From Email.');
      return;
    }
    
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/api/email/settings`, 
      payload, 
      {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        timeout: 10000
      }
    );
    
    console.log('✅ Save successful! Response:', response.data);
    
    if (activeTab === 'signature') {
      alert('✅ Email signature saved successfully!');
    } else {
      alert('✅ Email settings saved successfully!');
    }
    
    // Clear password field after save for security
    setSettings(prev => ({ ...prev, smtp_password: '' }));
    
  } catch (error: any) {
    console.error('❌ Save failed!');
    
    if (error.response?.data?.message) {
      alert(`❌ ${error.response.data.message}`);
    } else {
      alert(`❌ Server error: ${error.message}`);
    }
  } finally {
    setLoading(false);
  }
};

const handleSaveSignature = async () => {
  setLoading(true);
  
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      alert('No authentication token found. Please log in again.');
      return;
    }
    
    // 1. Load current settings to check if they exist
    const currentResponse = await axios.get(
      `${process.env.REACT_APP_API_URL}/api/email/settings`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    
    const currentSettings = currentResponse.data.settings || {};
    
        // If password field is empty, backend keeps the existing one.
    // If no config exists yet, backend returns a clear 400.
    const password: string | undefined = settings.smtp_password && settings.smtp_password.trim() !== ''
      ? settings.smtp_password
      : undefined;
    
    // Build payload
    const payload: any = {
      smtp_host: settings.smtp_host || currentSettings.smtp_host || '',
      smtp_port: settings.smtp_port || currentSettings.smtp_port || 587,
      use_tls: settings.use_tls !== false,
      use_ssl: settings.use_ssl || false,
      smtp_username: settings.smtp_username || currentSettings.smtp_username || '',
      from_email: settings.from_email || currentSettings.from_email || '',
      from_name: settings.from_name || currentSettings.from_name || '',
      email_signature: settings.email_signature || '',
      signature_type: settings.signature_type || 'html',
      include_signature: settings.include_signature !== false
    };
    
    // Only include password if we have one (first time setup or changing)
    if (password) {
      payload.smtp_password = password;
    }
    // If password is undefined, backend should keep existing password
    
        console.log('📦 Signature save payload:', {
      ...payload,
      smtp_password: password ? '***' : '[KEEP EXISTING]',
      email_signature: payload.email_signature ? 'Has content' : 'Empty'
    });
    
    // 4. Save
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/api/email/settings`, 
      payload, 
      {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        timeout: 10000
      }
    );
    
    console.log('✅ Signature saved!', response.data);
    alert('✅ Email signature saved successfully!');
    
    // Clear password from state for security
    setSettings(prev => ({ ...prev, smtp_password: '' }));
    
    // Reload settings to get updated values
    await loadCurrentSettings();
    
  } catch (error: any) {
    console.error('❌ Signature save failed:', error);
    
    if (error.response?.data?.message) {
      alert(`❌ ${error.response.data.message}`);
    } else {
      alert(`❌ Error: ${error.message}`);
    }
  } finally {
    setLoading(false);
  }
};

  const handleTest = async () => {
    setLoading(true);
    setTestResult(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert('No authentication token found');
        return;
      }
      
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/email/test`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTestResult(response.data);
    } catch (error: any) {
      setTestResult({ 
        success: false, 
        message: 'Test failed: ' + (error.response?.data?.message || error.message) 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAdvancedTest = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/email/test-advanced`, 
        settings, 
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // Show results in a modal or alert
      alert(JSON.stringify(response.data.results, null, 2));
    } catch (error: any) {
      alert('Advanced test failed: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

    const handleSendTest = async () => {
  if (!settings.from_email) {
    alert('Please enter a "From" email address first');
    return;
  }

  setLoading(true);
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      alert('No authentication token found');
      return;
    }
    
    // Backend uses the SAVED config — we only need to specify the recipient.
    // If you haven't saved your settings yet, you'll get a "not configured" error.
    const payload = {
      to: settings.from_email
    };
    
    const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/email/send-test`, payload, {
      headers: { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      timeout: 30000
    });
    
    if (response.data.success) {
      alert('✅ Test email sent! Please check your inbox (and spam folder).');
    } else {
      alert('❌ Failed to send test email: ' + (response.data.message || 'Unknown error'));
    }
    } catch (error: any) {
    console.error('Test email error:', error.response?.data || error.message);
    
    // Distinguish "not configured" from other failures
    if (error.response?.status === 412 && error.response?.data?.code === 'EMAIL_NOT_CONFIGURED') {
      alert('❌ You haven\'t saved your email settings yet. Fill in the form above and click "Save All Settings" first, then try sending a test email.');
    } else {
      alert('❌ Failed to send test email: ' + (error.response?.data?.message || error.response?.data?.error || error.message));
    }
  } finally {
    setLoading(false);
  }
};

  const handleInsertTemplate = (templateType: string) => {
    let template = '';
    
    switch(templateType) {
      case 'simple':
        template = `<div style="font-family: Arial, sans-serif; color: #333;">
  <p>Best regards,</p>
  <p><strong>${settings.from_name || '[Your Name]'}</strong></p>
  <p>${settings.from_email || '[Your Email]'}</p>
  <p>${settings.from_email ? settings.from_email.split('@')[1] || '[Company]' : '[Company]'}</p>
</div>`;
        break;
      case 'professional':
        template = `<div style="font-family: Arial, sans-serif; color: #333; border-top: 2px solid #4F46E5; padding-top: 15px; margin-top: 20px;">
  <table style="width: 100%; max-width: 600px;">
    <tr>
      <td style="vertical-align: top; padding-right: 20px;">
        <img src="[LOGO_URL]" alt="Company Logo" style="max-width: 100px; height: auto;" />
      </td>
      <td style="vertical-align: top;">
        <p style="margin: 0 0 8px 0; font-weight: bold; color: #4F46E5;">${settings.from_name || '[Your Name]'}</p>
        <p style="margin: 0 0 4px 0; font-size: 14px; color: #666;">[Your Position]</p>
        <p style="margin: 0 0 4px 0; font-size: 14px; color: #666;">${settings.from_email || '[Your Email]'}</p>
        <p style="margin: 0 0 4px 0; font-size: 14px; color: #666;">[Phone Number]</p>
        <p style="margin: 0; font-size: 14px; color: #666;">[Company Website]</p>
      </td>
    </tr>
  </table>
</div>`;
        break;
      case 'minimal':
        template = `<p style="font-family: Arial, sans-serif; color: #666; font-size: 14px; margin-top: 20px; border-top: 1px solid #eee; padding-top: 10px;">
  ---<br/>
  <strong>${settings.from_name || '[Your Name]'}</strong><br/>
  ${settings.from_email || '[Your Email]'}
</p>`;
        break;
      default:
        template = '';
    }
    
    setSettings(prev => ({
      ...prev,
      email_signature: prev.email_signature + template
    }));
  };

  const presetConfigs = {
    gmail: {
      smtp_host: 'smtp.gmail.com',
      smtp_port: 587,
      use_tls: true,
      use_ssl: false,
      note: 'Use App Password if 2FA enabled'
    },
    outlook: {
      smtp_host: 'smtp-mail.outlook.com',
      smtp_port: 587,
      use_tls: true,
      use_ssl: false,
      note: 'Office 365/Outlook.com'
    },
    office365: {
      smtp_host: 'smtp.office365.com',
      smtp_port: 587,
      use_tls: true,
      use_ssl: false,
      note: 'Microsoft 365'
    }
  };

  return (
    <div className={`flex-1 flex flex-col transition-all duration-300 ${isSidebarVisible ? "ml-64" : "ml-0"}`}>
      <Sidebar
        isVisible={isSidebarVisible}
        toggleSidebar={() => setIsSidebarVisible(!isSidebarVisible)}
      />

      <div className="flex-1 flex flex-col">
        <Header
          title="Email Configuration"
          user={user}
          toggleSidebar={() => setIsSidebarVisible(!isSidebarVisible)}
          isSidebarVisible={isSidebarVisible}
        />

        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-8">
            {/* Tab Navigation */}
            <div className="mb-6 border-b border-gray-200">
              <nav className="-mb-px flex space-x-8">
                <button
                  onClick={() => setActiveTab('settings')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'settings'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  SMTP Settings
                </button>
                <button
                  onClick={() => setActiveTab('signature')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'signature'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Email Signature
                </button>
              </nav>
            </div>

            {/* SMTP Settings Tab */}
            {activeTab === 'settings' && (
              <>
                <h1 className="text-2xl font-bold text-gray-800 mb-2">Email Settings</h1>
                <p className="text-gray-600 mb-6">Configure your SMTP server to send emails from the system</p>
                
                {/* Quick Presets */}
                <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                  <h3 className="font-medium text-blue-800 mb-2">Quick Setup Presets</h3>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(presetConfigs).map(([name, preset]) => (
                      <button
                        key={name}
                        onClick={() => setSettings(prev => ({ ...prev, ...preset }))}
                        className="px-3 py-1 bg-white border border-blue-200 text-blue-700 rounded text-sm hover:bg-blue-50"
                      >
                        {name.charAt(0).toUpperCase() + name.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Settings Form */}
                <div className="space-y-6">
                  {/* SMTP Host */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      SMTP Host *
                    </label>
                    <input
                      type="text"
                      value={settings.smtp_host}
                      onChange={(e) => setSettings({...settings, smtp_host: e.target.value})}
                      placeholder="smtp.gmail.com"
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  {/* Port and Security */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        SMTP Port *
                      </label>
                      <select
                        value={settings.smtp_port}
                        onChange={(e) => setSettings({...settings, smtp_port: parseInt(e.target.value)})}
                        className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="25">25 (Unsecured)</option>
                        <option value="587">587 (TLS - Recommended)</option>
                        <option value="465">465 (SSL)</option>
                      </select>
                    </div>

                    <div className="mt-4">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={settings.allow_self_signed || false}
                          onChange={(e) => setSettings({...settings, allow_self_signed: e.target.checked})}
                          className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                        />
                        <span className="ml-2 text-gray-700">
                          Allow self-signed certificates
                        </span>
                        <span className="ml-2 text-xs text-gray-500">
                          (Required for some corporate/development SMTP servers)
                        </span>
                      </label>
                    </div>
                    
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Security Options
                      </label>
                      <div className="flex gap-6">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={settings.use_tls}
                            onChange={(e) => setSettings({...settings, use_tls: e.target.checked})}
                            className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                          />
                          <span className="ml-2 text-gray-700">Use TLS</span>
                        </label>
                        
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={settings.use_ssl}
                            onChange={(e) => setSettings({...settings, use_ssl: e.target.checked})}
                            className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                          />
                          <span className="ml-2 text-gray-700">Use SSL</span>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Username and Password */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        SMTP Username *
                      </label>
                      <input
                        type="text"
                        value={settings.smtp_username}
                        onChange={(e) => setSettings({...settings, smtp_username: e.target.value})}
                        placeholder="your-email@domain.com"
                        className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        SMTP Password {settings.smtp_password === '' ? '(leave blank to keep current)' : '*'}
                      </label>
                      <input
                        type="password"
                        value={settings.smtp_password}
                        onChange={(e) => setSettings({...settings, smtp_password: e.target.value})}
                        placeholder="•••••••• (leave blank to keep current password)"
                        className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="mt-1 text-sm text-gray-500">
                        {settings.smtp_password === '' 
                          ? 'Will keep existing password' 
                          : 'Will update password'}
                      </p>
                    </div>

                  </div>

                  {/* From Email and Name */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        From Email *
                      </label>
                      <input
                        type="email"
                        value={settings.from_email}
                        onChange={(e) => setSettings({...settings, from_email: e.target.value})}
                        placeholder="sender@yourdomain.com"
                        className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        From Name
                      </label>
                      <input
                        type="text"
                        value={settings.from_name}
                        onChange={(e) => setSettings({...settings, from_name: e.target.value})}
                        placeholder="Your Company Name"
                        className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  {/* Test Results */}
                  {testResult && (
                    <div className={`p-4 rounded-lg ${testResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                      <div className="flex items-center">
                        <div className={`flex-shrink-0 h-5 w-5 rounded-full ${testResult.success ? 'bg-green-500' : 'bg-red-500'}`}></div>
                        <div className="ml-3">
                          <p className={`text-sm font-medium ${testResult.success ? 'text-green-800' : 'text-red-800'}`}>
                            {testResult.success ? '✓ Success' : '✗ Error'}
                          </p>
                          <p className={`text-sm ${testResult.success ? 'text-green-700' : 'text-red-700'}`}>
                            {testResult.message}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-3 pt-6">
                    <button
                      onClick={handleTest}
                      disabled={loading}
                      className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
                    >
                      {loading ? 'Testing...' : 'Test Connection'}
                    </button>

                    <button
                      onClick={handleAdvancedTest}
                      disabled={loading}
                      className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50 text-sm"
                    >
                      Advanced Test
                    </button>
                    
                                        <div className="flex flex-col items-start gap-1">
                      <button
                        onClick={handleSendTest}
                        disabled={loading || !settings.smtp_host}
                        className="px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium"
                      >
                        Send Test Email
                      </button>
                      <span className="text-xs text-gray-500">
                        Uses your saved settings. Save first if you've made changes.
                      </span>
                    </div>
                    
                    <button
                      onClick={handleSave}
                      disabled={loading}
                      className="px-5 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 font-medium ml-auto"
                    >
                      {loading ? 'Saving...' : 'Save All Settings'}
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* Email Signature Tab */}
            {activeTab === 'signature' && (
              <>
                <div className="mb-6">
                  <h1 className="text-2xl font-bold text-gray-800 mb-2">Email Signature</h1>
                  <p className="text-gray-600">Create a custom signature that will be added to all outgoing emails</p>
                </div>

                {/* Signature Controls */}
                <div className="mb-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={settings.include_signature}
                          onChange={(e) => setSettings({...settings, include_signature: e.target.checked})}
                          className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                        />
                        <span className="ml-2 text-gray-700 font-medium">
                          Include signature in emails
                        </span>
                      </label>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      <span className="text-sm text-gray-600">Format:</span>
                      <div className="flex border border-gray-300 rounded-md overflow-hidden">
                        <button
                          onClick={() => setSettings({...settings, signature_type: 'html'})}
                          className={`px-3 py-1 text-sm ${settings.signature_type === 'html' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`}
                        >
                          HTML
                        </button>
                        <button
                          onClick={() => setSettings({...settings, signature_type: 'text'})}
                          className={`px-3 py-1 text-sm ${settings.signature_type === 'text' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`}
                        >
                          Plain Text
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Signature Templates */}
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h3 className="font-medium text-blue-800 mb-2">Quick Templates</h3>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => handleInsertTemplate('simple')}
                        className="px-3 py-1.5 bg-white border border-blue-200 text-blue-700 rounded text-sm hover:bg-blue-50"
                      >
                        Simple Signature
                      </button>
                      <button
                        onClick={() => handleInsertTemplate('professional')}
                        className="px-3 py-1.5 bg-white border border-blue-200 text-blue-700 rounded text-sm hover:bg-blue-50"
                      >
                        Professional Layout
                      </button>
                      <button
                        onClick={() => handleInsertTemplate('minimal')}
                        className="px-3 py-1.5 bg-white border border-blue-200 text-blue-700 rounded text-sm hover:bg-blue-50"
                      >
                        Minimal Style
                      </button>
                    </div>
                  </div>
                </div>

                {/* Signature Editor */}
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <label className="block text-sm font-medium text-gray-700">
                      Signature Content {settings.signature_type === 'html' && '(HTML allowed)'}
                    </label>
                    <button
                      onClick={() => setShowSignaturePreview(!showSignaturePreview)}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      {showSignaturePreview ? 'Hide Preview' : 'Show Preview'}
                    </button>
                  </div>
                  
                  {/* HTML Editor */}
                  {settings.signature_type === 'html' ? (
                    <div className="space-y-4">
                      <textarea
                        value={settings.email_signature}
                        onChange={(e) => setSettings({...settings, email_signature: e.target.value})}
                        placeholder="Enter your HTML signature here. You can use HTML tags like &lt;strong&gt;, &lt;em&gt;, &lt;a&gt;, etc."
                        className="w-full h-64 border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                        rows={10}
                      />
                      
                      {/* HTML Toolbar */}
                      <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm text-gray-600 mr-2">Quick HTML:</span>
                        <button
                          onClick={() => setSettings(prev => ({...prev, email_signature: prev.email_signature + '<strong>Bold text</strong>'}))}
                          className="px-3 py-1 bg-white border border-gray-300 text-gray-700 rounded text-sm hover:bg-gray-50"
                        >
                          Bold
                        </button>
                        <button
                          onClick={() => setSettings(prev => ({...prev, email_signature: prev.email_signature + '<em>Italic text</em>'}))}
                          className="px-3 py-1 bg-white border border-gray-300 text-gray-700 rounded text-sm hover:bg-gray-50"
                        >
                          Italic
                        </button>
                        <button
                          onClick={() => setSettings(prev => ({...prev, email_signature: prev.email_signature + '<a href="https://example.com">Link</a>'}))}
                          className="px-3 py-1 bg-white border border-gray-300 text-gray-700 rounded text-sm hover:bg-gray-50"
                        >
                          Link
                        </button>
                        <button
                          onClick={() => setSettings(prev => ({...prev, email_signature: prev.email_signature + '<br/>'}))}
                          className="px-3 py-1 bg-white border border-gray-300 text-gray-700 rounded text-sm hover:bg-gray-50"
                        >
                          Line Break
                        </button>
                        <button
                          onClick={() => setSettings(prev => ({...prev, email_signature: prev.email_signature + '<div style="border-top: 1px solid #ccc; margin: 10px 0;"></div>'}))}
                          className="px-3 py-1 bg-white border border-gray-300 text-gray-700 rounded text-sm hover:bg-gray-50"
                        >
                          Divider
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* Plain Text Editor */
                    <textarea
                      value={settings.email_signature}
                      onChange={(e) => setSettings({...settings, email_signature: e.target.value})}
                      placeholder="Enter your plain text signature here"
                      className="w-full h-64 border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows={10}
                    />
                  )}

                  {/* Signature Preview */}
                  {showSignaturePreview && settings.email_signature && (
                    <div className="mt-6 p-4 border border-gray-200 rounded-lg bg-white">
                      <h4 className="font-medium text-gray-700 mb-3">Signature Preview:</h4>
                      <div className="p-4 bg-gray-50 rounded border border-gray-100">
                        {settings.signature_type === 'html' ? (
                          <div 
                            dangerouslySetInnerHTML={{ __html: settings.email_signature }}
                            className="email-signature-preview"
                          />
                        ) : (
                          <div className="whitespace-pre-wrap font-mono text-sm">
                            {settings.email_signature}
                          </div>
                        )}
                      </div>
                      <p className="mt-2 text-sm text-gray-500">
                        This is how your signature will appear in emails
                      </p>
                    </div>
                  )}

                  {/* Signature Info */}
                  <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <h4 className="font-medium text-gray-700 mb-2">Signature Tips:</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Your signature will be automatically added to the bottom of all outgoing emails</li>
                      <li>• For HTML signatures, use inline styles for best compatibility across email clients</li>
                      <li>• Keep your signature concise - 4-5 lines is recommended</li>
                      <li>• Include your name, position, contact information, and company details</li>
                      <li>• Test your signature by sending a test email</li>
                    </ul>
                  </div>

                  {/* Save Button for Signature */}
                  <div className="flex justify-end pt-6">

                    <button
                      onClick={handleSaveSignature}
                      disabled={loading}
                      className="px-5 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 font-medium"
                    >
                      {loading ? 'Saving...' : 'Save Signature'}
                    </button>

                  </div>
                </div>
              </>
            )}

            {/* Help Section */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <h3 className="font-medium text-gray-700 mb-3">Need Help?</h3>
              <ul className="text-sm text-gray-600 space-y-2">
                <li>• <strong>Gmail:</strong> Enable 2-factor authentication, then generate an "App Password"</li>
                <li>• <strong>Outlook/Office 365:</strong> Use your email and password (2FA may require app password)</li>
                <li>• <strong>cPanel/Web Hosting:</strong> Use credentials provided by your hosting provider</li>
                <li>• <strong>Signature:</strong> Your signature will be automatically included in all system emails</li>
                <li>• Test connection first, then send a test email to verify both SMTP and signature</li>
              </ul>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

// Make sure this export is at the bottom
export default EmailSettingsPage;